import React, { useEffect, useState } from "react";
import { Plus, Trash2, Wrench } from "lucide-react";
import { toast } from "react-hot-toast";
import useOrders from "../../Services/OrderServices/useOrders";

const cls = (...a) => a.filter(Boolean).join(" ");

const Customization = ({ userID, active = false }) => {
  const {
    getPosOrderCustomizations,
    addPosOrderCustomization,
    deletePosOrderCustomization,
  } = useOrders();

  const [customizations, setCustomizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [form, setForm] = useState({
    customization: "",
    customizationType: "",
  });

  const loadCustomizations = async () => {
    if (!userID) {
      setCustomizations([]);
      return;
    }

    setLoading(true);
    try {
      const res = await getPosOrderCustomizations(userID);
      if (res.success) {
        setCustomizations(res.data || []);
      } else {
        setCustomizations([]);
        toast.error(res.message);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load customizations");
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomization = async () => {
    if (!userID) return toast.error("Please select a client first.");
    if (!form.customization.trim() || !form.customizationType) {
      return toast.error("Please fill customization and type.");
    }

    setAddLoading(true);
    try {
      const res = await addPosOrderCustomization({
        userid: userID,
        customization: form.customization.trim(),
        customizationType: form.customizationType,
      });

      if (res.success) {
        toast.success(res.message);
        setForm({
          customization: "",
          customizationType: "",
        });
        await loadCustomizations();
      } else {
        toast.error(res.message);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to add customization");
    } finally {
      setAddLoading(false);
    }
  };

  const handleDeleteCustomization = async (customizationId) => {
    if (!userID) return toast.error("Please select a client first.");

    setLoading(true);
    try {
      const res = await deletePosOrderCustomization({
        userid: userID,
        customizationId,
      });

      if (res.success) {
        toast.success(res.message);
        await loadCustomizations();
      } else {
        toast.error(res.message);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to delete customization");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!active) return;
    loadCustomizations();
  }, [userID, active]);

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border bg-gray-50 p-4">
        <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-[#01C37D]">
          <Wrench size={16} />
          Add Customization
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[1fr_220px_auto]">
          <div>
            <div className="mb-1 text-xs text-gray-500">Customization</div>
            <input
              type="text"
              value={form.customization}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, customization: e.target.value }))
              }
              className="h-10 w-full rounded-xl border bg-white px-3 text-sm outline-none"
              placeholder="e.g. Extra Spicy, No Onion"
              disabled={addLoading}
            />
          </div>

          <div>
            <div className="mb-1 text-xs text-gray-500">Customization Type</div>
            <select
              value={form.customizationType}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, customizationType: e.target.value }))
              }
              className="h-10 w-full rounded-xl border bg-white px-3 text-sm outline-none"
              disabled={addLoading}
            >
              <option value="">Select Type</option>
              <option value="customize">Customize</option>
              <option value="add-ons">Add-ons</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={handleAddCustomization}
              disabled={addLoading}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-[#01C37D] px-4 text-sm font-medium text-white hover:bg-[#01C37D]/90 disabled:opacity-60"
            >
              <Plus size={16} />
              {addLoading ? "Adding…" : "Add"}
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border bg-white">
        <div className="border-b px-4 py-3 text-sm font-semibold">
          Existing Customizations
        </div>

        <div className="p-4">
          {loading ? (
            <div className="py-8 text-center text-sm text-gray-500">Loading customizations…</div>
          ) : customizations.length > 0 ? (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {customizations.map((item, index) => (
                <div key={item.id || index} className="rounded-2xl border p-4">
                  <div className="mb-3 flex items-start justify-between gap-3 border-b pb-2">
                    <div>
                      <div className="text-sm font-semibold text-[#01C37D]">
                        {item.customization}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        {item.customization_type}
                      </div>
                    </div>

                    <button
                      onClick={() => handleDeleteCustomization(item.id)}
                      disabled={loading}
                      className="inline-flex items-center gap-1 rounded-lg bg-rose-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                    >
                      <Trash2 size={13} />
                      Remove
                    </button>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between gap-3">
                      <span className="text-gray-500">Customization</span>
                      <span className="font-medium text-right">{item.customization}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-gray-500">Type</span>
                      <span className="font-medium text-right">{item.customization_type}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed p-6 text-sm text-gray-500">
              No customizations found.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Customization;